const BASE_URL = '/api';

export const ApiService = {
    async getProducts(query: string = '') {
        const q = query ? `?q=${encodeURIComponent(query)}` : '';
        const res = await this._fetch(`/products${q}`);
        // Enforce structural integrity for the frontend: always return an array
        if (res && res.success && Array.isArray(res.data)) return res;
        return { success: true, data: [] };
    },

    async getProductById(id: string) {
        return this._fetch(`/products/${id}`);
    },

    async login(credentials: any) {
        const res = await this._fetch('/auth/login', { method: 'POST', body: JSON.stringify(credentials) });
        if (res?.success && res.user) {
            localStorage.setItem('auth_user', JSON.stringify(res.user));
            if (res.token) localStorage.setItem('auth_token', res.token);
        }
        return res;
    },

    async adminLogin(credentials: any) {
        const res = await this._fetch('/admin/auth/login', { method: 'POST', body: JSON.stringify(credentials) });
        if (res?.success && res.user) {
            localStorage.setItem('auth_user', JSON.stringify(res.user));
            if (res.token) localStorage.setItem('auth_token', res.token);
        }
        return res;
    },

    async register(userData: any) {
        const res = await this._fetch('/auth/register', { method: 'POST', body: JSON.stringify(userData) });
        if (res?.success && res.user) {
            localStorage.setItem('auth_user', JSON.stringify(res.user));
            if (res.token) localStorage.setItem('auth_token', res.token);
        }
        return res;
    },

    async logout() {
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_token');
        this._fetch('/auth/logout', { method: 'POST' }).catch(() => { });
        return { success: true };
    },

    async getProfile() {
        const userJson = localStorage.getItem('auth_user');
        if (!userJson) return { success: false, user: null };
        try {
            return { success: true, user: JSON.parse(userJson) };
        } catch {
            return { success: false, user: null };
        }
    },

    async updateProfile(data: { name: string; email: string }) {
        return this._fetch('/auth/profile', { method: 'PUT', body: JSON.stringify(data) });
    },

    async changePassword(data: { currentPassword: string; newPassword: string }) {
        return this._fetch('/auth/password', { method: 'PUT', body: JSON.stringify(data) });
    },

    async getMyOrders() {
        return this._fetch('/orders', { method: 'GET' }, true);
    },

    async getFavorites() {
        return this._fetch('/favorites', { method: 'GET' }, true);
    },

    async addFavorite(productId: number) {
        return this._fetch('/favorites', { method: 'POST', body: JSON.stringify({ productId }) });
    },

    async removeFavorite(productId: number) {
        return this._fetch(`/favorites/${productId}`, { method: 'DELETE' });
    },

    async getReviews(productId: number | string) {
        return this._fetch(`/reviews/${productId}`, { method: 'GET' }, true);
    },

    async addReview(productId: number, rating: number, comment?: string) {
        return this._fetch('/reviews', { method: 'POST', body: JSON.stringify({ productId, rating, comment }) });
    },

    async getCart() {
        return this._fetch('/cart', { method: 'GET' }, true);
    },

    async syncCart(items: { productId: number, quantity: number }[]) {
        return this._fetch('/cart/sync', { method: 'POST', body: JSON.stringify(items) });
    },

    async checkout(shippingAddress: string, paymentMethod: string) {
        return this._fetch('/orders/checkout', {
            method: 'POST',
            body: JSON.stringify({ shippingAddress, paymentMethod })
        });
    },

    // --- Admin Routes ---

    async createProduct(data: any) {
        return this._fetch('/products', { method: 'POST', body: JSON.stringify(data) });
    },

    async updateProduct(id: number | string, data: any) {
        return this._fetch(`/products/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    },

    async deleteProduct(id: number | string) {
        return this._fetch(`/products/${id}`, { method: 'DELETE' });
    },

    async setProductMedia(id: number | string, mediaIds: number[]) {
        return this._fetch(`/products/${id}/media`, { method: 'PUT', body: JSON.stringify({ mediaIds }) });
    },

    async getCategories() {
        return this._fetch('/categories');
    },

    async createCategory(data: any) {
        return this._fetch('/categories', { method: 'POST', body: JSON.stringify(data) });
    },

    async updateCategory(id: number | string, data: any) {
        return this._fetch(`/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    },

    async deleteCategory(id: number | string) {
        return this._fetch(`/categories/${id}`, { method: 'DELETE' });
    },

    // --- Media Routes ---
    async getMedia() {
        return this._fetch('/media');
    },
    
    async uploadMedia(file: File) {
        // Special fetch because body is FormData, not JSON
        const token = localStorage.getItem('auth_token');
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await fetch(`${BASE_URL}/media/upload`, {
            method: 'POST',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            body: formData
        });
        return res.json().catch(() => null);
    },

    async deleteMedia(id: number | string) {
        return this._fetch(`/media/${id}`, { method: 'DELETE' });
    },

    // --- Promotions Routes ---
    async getActivePromotions() {
        return this._fetch('/promotions/active');
    },

    async getAllPromotions() {
        return this._fetch('/promotions');
    },

    async createPromotion(data: any) {
        return this._fetch('/promotions', { method: 'POST', body: JSON.stringify(data) });
    },

    async updatePromotion(id: number | string, data: any) {
        return this._fetch(`/promotions/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    },

    async deletePromotion(id: number | string) {
        return this._fetch(`/promotions/${id}`, { method: 'DELETE' });
    },

    // --- Admin Modules ---
    async getAdminOrders() {
        return this._fetch('/admin/orders');
    },

    async updateOrderStatus(id: number | string, status: string) {
        return this._fetch(`/admin/orders/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
    },

    async getAdminUsers() {
        return this._fetch('/admin/users');
    },

    async getAnalytics() {
        return this._fetch('/admin/analytics');
    },

    // silent=true suppresses console.error (e.g. expected 401 on /me)
    async _fetch(endpoint: string, options: RequestInit = {}, silent = false) {
        try {
            const url = `${BASE_URL}${endpoint}`;
            const config = {
                ...options,
                credentials: 'include' as RequestCredentials,
                headers: {
                    'Content-Type': 'application/json',
                    ...options.headers,
                } as Record<string, string>
            };
            
            const token = localStorage.getItem('auth_token');
            if (token && token !== 'undefined' && token !== 'null') {
                config.headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(url, config);
            const data = await response.json().catch(() => null);
            if (!silent) console.log(url, data);

            if (response.status === 401) {
                localStorage.removeItem('auth_user');
                localStorage.removeItem('auth_token');
            }

            if (!response.ok) {
                // If it's a standard user error (400-499), return the JSON gracefully to avoid console noise.
                if (response.status >= 400 && response.status < 500 && data) {
                    return data;
                }
                // Otherwise (500s), throw a hard error.
                throw new Error(data?.message || data?.error || `HTTP error ${response.status}`);
            }

            // For endpoints that return { success: boolean, data: T | message: string },
            // we catch any silent operational failures that passed the HTTP 200 check.
            if (data && typeof data.success === 'boolean' && !data.success) {
                return data; // Return gracefully instead of throwing
            }

            return data;
        } catch (error) {
            // Only log actual crashes, network failures, or 500 errors
            if (!silent) console.error(`API Exception (${endpoint}):`, error);
            throw error;
        }
    }
};
